import { useState } from 'react';
import type { ServiceCategory } from '@makhe/shared';
import { wearLifeFor } from '@/lib/wearLife';

interface Props {
  category: ServiceCategory;
  // Wear-life values (in weeks) pulled from reviews that reported one.
  wearWeeksValues: number[];
  // Cheapest tier price, used to frame longevity as cost-per-week.
  fromPriceZARcents: number;
}

const fmtWeeks = (w: number) => (Number.isInteger(w) ? `${w}` : w.toFixed(1));

// Surfaces "how long does it actually last" — the question buyers of braids,
// nails and lash sets really care about, and one a generic booking app can't
// answer. Computed from verified reviews; hidden until at least one buyer has
// reported a wear time, and only for categories where longevity is meaningful.
export function ServiceLongevity({ category, wearWeeksValues, fromPriceZARcents }: Props) {
  const [open, setOpen] = useState(false);
  const meta = wearLifeFor(category);
  if (!meta || wearWeeksValues.length === 0) return null;

  const avg = wearWeeksValues.reduce((a, b) => a + b, 0) / wearWeeksValues.length;
  const pct = Math.max(5, Math.min(98, (avg / meta.scaleMax) * 100));
  const minPct = (meta.min / meta.scaleMax) * 100;
  const maxPct = (meta.max / meta.scaleMax) * 100;
  const perWeek = avg > 0 ? Math.round(fromPriceZARcents / avg / 100) : null;

  const verdict =
    avg >= meta.max
      ? 'Lasts longer than most'
      : avg >= meta.min
        ? 'Right in the typical range'
        : 'On the shorter side';

  return (
    <div className="mt-10 rounded-xl border border-makhe-line bg-makhe-paper p-6">
      <div className="flex items-start justify-between">
        <div>
          <p className="eyebrow">Service longevity</p>
          <p className="mt-1 text-sm text-makhe-muted">Average wear, from verified reviews</p>
        </div>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-label="How this is measured"
          className="grid h-6 w-6 place-items-center rounded-full border border-makhe-line text-xs text-makhe-muted transition-colors hover:text-makhe-ink"
        >
          i
        </button>
      </div>

      <div className="mt-4 flex items-baseline gap-2">
        <span
          className="font-display text-6xl leading-none text-makhe-ochre"
          style={{ fontVariationSettings: '"opsz" 144, "wght" 350' }}
        >
          {fmtWeeks(avg)}
        </span>
        <span className="font-display text-2xl text-makhe-ink/70">weeks</span>
        <span className="ml-auto self-center rounded-full bg-makhe-forest/15 px-3 py-1 text-[11px] font-semibold uppercase tracking-widest text-makhe-forest">
          {verdict}
        </span>
      </div>

      {/* Gradient bar with the category's typical band marked, plus this
          service's average as a marker on top. */}
      <div className="mt-6">
        <div className="relative h-2.5 w-full rounded-full bg-gradient-to-r from-makhe-plum via-makhe-ochre to-makhe-forest">
          {/* typical-range band */}
          <span
            className="absolute top-1/2 h-5 -translate-y-1/2 rounded-full border-x-2 border-white/30"
            style={{ left: `${minPct}%`, width: `${maxPct - minPct}%` }}
            aria-hidden
          />
          {/* this service's average */}
          <span
            className="absolute top-1/2 h-5 w-5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-makhe-paper bg-white shadow-lift"
            style={{ left: `${pct}%` }}
            aria-hidden
          />
        </div>
        <div className="mt-2 flex justify-between text-[11px] uppercase tracking-widest text-makhe-muted">
          <span>1 wk</span>
          <span>
            Typical {meta.min}–{meta.max} wks
          </span>
          <span>{meta.scaleMax}+ wks</span>
        </div>
      </div>

      <div className="mt-5 flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-makhe-line pt-4 text-sm">
        {perWeek != null && (
          <span className="font-mono text-makhe-ink">
            ≈ R{perWeek}
            <span className="text-makhe-muted"> / week</span>
          </span>
        )}
        <span className="text-makhe-muted">
          from {wearWeeksValues.length} verified review{wearWeeksValues.length === 1 ? '' : 's'}
        </span>
      </div>

      {open && (
        <p className="mt-4 rounded-lg bg-makhe-bg/60 p-3 text-[13px] leading-relaxed text-makhe-ink/75">
          We average the wear time buyers report when they review — only from people who actually
          booked and paid. The band shows what {meta.noun.replace('this ', '')} usually lasts, so you
          can see how this creator compares. Cost-per-week uses the lowest price tier.
        </p>
      )}
    </div>
  );
}
